import {
  Component, OnInit, Input, OnChanges, OnDestroy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { RequirementsService } from 'src/app/services/requirements.service';
import { CoursesService } from 'src/app/services/courses.service';
import { FormattedCourse, formatCourse } from '../../../../my-courses.utils';
import { NewTableData } from 'src/app/components/new-table/new-table.component';
import {
  GeneralDataCell,
  StatusDataCell,
} from 'src/app/components/new-table/table-cell/table-cell.component';
import { UpdateRequirementModalComponent } from '../update-requirement-modal/update-requirement-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { UpdateStatusModalComponent } from '../update-status-modal/update-status-modal.component';
import { CatalogCourseDetailsComponent } from 'src/app/routes/manage-courses/components/course-catalog/components/catalog-course-details/catalog-course-details.component';
import { REGISTRATION_KIDS } from 'src/app/constants/registrationKids';
import { EntityService } from 'src/app/services/entity.service';

@Component({
  selector: 'app-requirements-list',
  templateUrl: './requirements-list.component.html',
  styleUrls: ['./requirements-list.component.scss'],
})
export class RequirementsListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: any;
  @Input() selectedYearChoice: any;
  @Input() disableEditingDueToYear: boolean;
  courseSubscription: Subscription;
  courses: any;
  formattedCourses: FormattedCourse[] = [];
  requirements = [];
  selectedRequirement = {
    requirementId: '',
    requirement: '',
    nodeType: '',
    children: [],
    requiredValue: 0,
    totalCompleted: 0,
    totalRegistered: 0,
    totalPending: 0,
    openingBalance: 0,
  };
  subrequirements = [];
  showSubRequirements = false;
  selectedItemsPerPage = 25;
  offset = 0;
  sortColumn = 'dateOffered';
  sortColumnDirection: 'ASC' | 'DESC' = 'ASC';
  tableData: NewTableData = {
    title: 'Course List',
    rowStyling: 'default',
    noDataFoundMessage: 'No courses found for the selected requirement.',
    noDataButtonText: 'Retry',
    columnTitles: [
      {
        name: 'Course',
        sortable: true,
        sortByProp: 'courseName',
      },
      {
        name: 'Date & Time',
        sortable: true,
        sortByProp: 'dateOffered',
        isDefaultSort: true,
        sortDirection: 'ASC',
      },
      {
        name: 'Requirement',
        sortable: true,
        sortByProp: 'requirement',
      },
      {
        name: 'Opening Balance',
        sortable: true,
        sortByProp: 'openingBalance',
      },
      {
        name: 'Hours',
        sortable: true,
        sortByProp: 'adjustedValue',
      },
      {
        name: 'Status',
        sortable: true,
        sortByProp: 'userCourseStatus',
      },
    ],
    data: [],
    hasBurgerMenu: true,
    burgerContent: [
      { content: 'View Course Details', eventName: 'viewDetails' },
      {
        content: 'Take Survey',
        eventName: 'takeSurvey',
        displayCondition: (itemId: number) => this.itemInSurveyStatus(itemId),
      },
      { content: 'Update Course Status', eventName: 'updateStatus' },
      { content: 'Change Requirement', eventName: 'changeRequirement' },
    ],

    meta: {
      totalItems: 0,
      itemsPerPage: 0,
      curPage: 0,
      totalPages: 0,
      usePagination: false,
      links: {
        prev: '',
        next: '',
        first: '',
        last: '',
        self: '',
      },
    },
  };

  entityTimezoneSubscription: Subscription;

  constructor(
    public dialog: MatDialog,
    private requirementService: RequirementsService,
    private courseService: CoursesService,
    private entityService: EntityService,
  ) { }

  private formatCoursesWithTimezone(): void {
    if (!this.courses?.rows) return;
    const tz = this.entityService.getLocalTimezoneWithFallback();
    this.formattedCourses = this.courses.rows.map((course: any) => formatCourse(course, tz));
    this.updateTableData();
  }

  ngOnInit(): void {
    if (!this.entityService.getLocalTimezone()) {
      this.entityService.getEntity('Config');
      this.entityTimezoneSubscription = this.entityService.entityData$.subscribe((entity: any) => {
        if (!entity) return;
        if (this.courses?.rows) this.formatCoursesWithTimezone();
        this.entityTimezoneSubscription?.unsubscribe();
      });
    }

    this.courseService.clearCoursesData();
    this.tableData.data = [];
    this.getRequirements();
    this.courseSubscription = this.courseService.coursesData$.subscribe((courses: any) => {
      if (!courses) return;
      this.courses = courses;
      const tz = this.entityService.getLocalTimezoneWithFallback();
      this.formattedCourses = courses.rows.map((course: any) => formatCourse(course, tz));
      this.updateTableData();
    });
  }

  ngOnDestroy(): void {
    this.entityTimezoneSubscription?.unsubscribe();
  }

  itemInSurveyStatus(registrationId: number) {
    const course = this.courses.rows.find((course: any) => {
      return course.courseItemId == registrationId;
    });

    return course.currentStatusKid === 'TAKE_SURVEY';
  }

  updateTableData() {
    const courses = this.formattedCourses;
    const courseData = courses.map((course: FormattedCourse) => {
      const courseId = course.courseItemId;

      const courseCell: GeneralDataCell = {
        type: 'general',
        mainText: course.name,
        subText: courseId,
      };
      const dateAndTimeCell: GeneralDataCell = {
        type: 'general',
        mainText: course.dateOffered || '-',
        subText: course.time || '-',
      };

      const requirementCell: GeneralDataCell = {
        type: 'general',
        mainText: '',
      };

      if (course.parentRequirement) {
        requirementCell.mainText = course.parentRequirement;
        requirementCell.subText = course.requirement;
      } else {
        requirementCell.mainText = course.requirement;
        requirementCell.subText = '-';
      }

      const openingBalanceCell: GeneralDataCell = {
        type: 'general',
        mainText: this.selectedRequirement.openingBalance || '-',
      };

      const hoursCell: GeneralDataCell = {
        type: 'general',
        mainText: course.hours || '-',
      };

      const statusCell: StatusDataCell = {
        type: 'status',
        statusKid: course.statusKid,
        statusDisplay: course.statusDisplay,
        statusType: 'courseRegistration',
        forceUppercase: true,
      };

      return {
        itemId: courseId,
        columnData: [
          courseCell,
          dateAndTimeCell,
          requirementCell,
          openingBalanceCell,
          hoursCell,
          statusCell,
        ],
      };
    });

    const meta: NewTableData['meta'] = {
      curPage: this.courses.pagination.curPage,
      itemsPerPage: this.courses.pagination.totalPerPage,
      links: this.courses.links,
      totalItems: this.courses.pagination.totalItems,
      totalPages: this.courses.pagination.totalPages,
      usePagination: true,
    };
    if (meta.totalPages > 1) {
      meta.usePagination = true;
    }

    this.tableData.meta = meta;
    this.tableData.data = courseData;
  }

  getRequirements = () => {
    this.requirementService
      .getUserFullRequirements(
        this.user.id, true, this.selectedYearChoice,
      )
      .subscribe((res: any) => {
        this.requirements = [];
        const groups = res.data.rows.GROUPS;
        const singles = res.data.rows.SINGLE;
        groups.forEach((group) => {
          const req = {
            ...group.Parent,
            children: group.Children,
            openingBalance: group.Parent.UserRequirement?.openingBalance || 0,
          };
          this.requirements.push(req);
        });

        // Process singles to include opening balance
        const processedSingles = singles.map((single) => ({
          ...single,
          openingBalance: single.UserRequirement?.openingBalance || 0,
        }));

        this.requirements = this.requirements.concat(processedSingles);

        if (this.selectedRequirement) {
          const updatedSelectedReq =
            this.requirements.find((req: any) =>
              req.requirementId == this.selectedRequirement.requirementId) || this.requirements[0];
          this.selectRequirement(updatedSelectedReq);
        } else {
          this.selectRequirement(this.requirements[0]);
        }
      });
  };

  ngOnChanges() {
    if (this.selectedYearChoice) {
      this.requirements = [];
      this.tableData.data = [];
      this.getRequirements();
      this.tableData.hasBurgerMenu = !this.disableEditingDueToYear;
    }
  }

  getSelectedRequirementType = () => {
    let type = 'Group';
    if (this.selectedRequirement.nodeType === 'SINGLE') {
      type = 'Single';
    }
    return type;
  };

  negativeStatuses = [
    REGISTRATION_KIDS.CANCELLED,
    REGISTRATION_KIDS.DENIED,
    REGISTRATION_KIDS.NOT_TAKEN,
    REGISTRATION_KIDS.REMOVED,
    REGISTRATION_KIDS.UNREGISTERED,
  ];
  selectRequirement = (req) => {
    this.selectedRequirement = req;
    const subreqs = [];
    req.children?.forEach((child) => {
      if (this.negativeStatuses.includes(child.Parent.statusKid)) {
        return;
      }
      subreqs.push({
        requirement: child.Parent.requirement,
        parent: '',
        requiredValue: child.Parent.requiredValue || 0,
        totalRegistered: child.totalRegistered || 0,
        totalCompleted: child.totalCompleted || 0,
      });
      child.Children?.forEach((subchild) => {
        if (this.negativeStatuses.includes(subchild.statusKid)) {
          return;
        }
        subreqs.push({
          requirement: subchild.requirement,
          parent: child.Parent.requirement,
          requiredValue: subchild.requiredValue || 0,
          totalRegistered: subchild.totalRegistered || 0,
          totalCompleted: subchild.totalCompleted || 0,
        });
      });
    });
    this.subrequirements = subreqs;
    this.searchForCourses();
  };

  getCompletedOutOfRequiredStat = () => {
    const currentYearCompleted = Number(this.selectedRequirement.totalCompleted);
    const openingBalance = Number(this.selectedRequirement.openingBalance || 0);
    const totalCompleted = currentYearCompleted + openingBalance;
    const required = Number(this.selectedRequirement.requiredValue);
    return `${totalCompleted}/${required} (${openingBalance} opening + ${currentYearCompleted} current)`;
  };

  getCompletedAmountWidth = () => {
    const currentYearCompleted = Number(this.selectedRequirement.totalCompleted);
    const openingBalance = Number(this.selectedRequirement.openingBalance || 0);
    const totalCompleted = currentYearCompleted + openingBalance;
    const required = Number(this.selectedRequirement.requiredValue);
    return `${(totalCompleted / required) * 100}%`;
  };

  getQueuedAmountWidth = () => {
    const registered = Number(this.selectedRequirement.totalRegistered);
    const pending = Number(this.selectedRequirement.totalPending);
    const queued = registered + pending;
    const required = Number(this.selectedRequirement.requiredValue);
    return `${(queued / required) * 100}%`;
  };

  getOpeningBalanceWidth = () => {
    const openingBalance = Number(this.selectedRequirement.openingBalance || 0);
    const required = Number(this.selectedRequirement.requiredValue);
    return `${(openingBalance / required) * 100}%`;
  };

  getCurrentYearCompletedWidth = () => {
    const currentYearCompleted = Number(this.selectedRequirement.totalCompleted);
    const required = Number(this.selectedRequirement.requiredValue);
    return `${(currentYearCompleted / required) * 100}%`;
  };

  getOpeningBalancePlusQueuedWidth = () => {
    const openingBalance = Number(this.selectedRequirement.openingBalance || 0);
    const registered = Number(this.selectedRequirement.totalRegistered);
    const pending = Number(this.selectedRequirement.totalPending);
    const queued = registered + pending;
    const required = Number(this.selectedRequirement.requiredValue);
    return `${((openingBalance + queued) / required) * 100}%`;
  };

  getOpeningBalancePlusCurrentYearWidth = () => {
    const openingBalance = Number(this.selectedRequirement.openingBalance || 0);
    const currentYearCompleted = Number(this.selectedRequirement.totalCompleted);
    const required = Number(this.selectedRequirement.requiredValue);
    return `${((openingBalance + currentYearCompleted) / required) * 100}%`;
  };

  searchForCourses() {
    const reqArray = [];
    // get all req ids from group
    if (this.selectedRequirement.nodeType === 'GROUP_PARENT') {
      this.selectedRequirement.children.forEach((child) => {
        if (child.Parent.nodeType === 'GROUP_LEAF') {
          reqArray.push(Number(child.Parent.requirementId));
        }
        if (child?.Children?.length > 0) {
          child.Children.forEach((grandchild) => {
            if (grandchild.nodeType === 'GROUP_LEAF') {
              reqArray.push(Number(grandchild.requirementId));
            }
          });
        }
      });
    } else {
      // handle single node reqs
      reqArray.push(Number(this.selectedRequirement.requirementId));
    }
    this.courseService.getPDPlanCourseRegistrations(
      {
        userId: this.user.id,
        sortDirection: this.sortColumnDirection,
        sortColumn: this.sortColumn,
        includedAssociations: 'Survey',
        requirementIdArr: reqArray,
        yearId: this.selectedYearChoice,
      },
      this.selectedItemsPerPage,
      this.offset,
    );
  }

  handleRowClicked(itemId: number) { }

  handleBurgerMenuClicked({
    eventName,
    itemId,
  }: {
    eventName: string;
    itemId: number;
  }) {
    const course = this.formattedCourses.find((course: FormattedCourse) => course.courseItemId === itemId);
    if (eventName === 'changeRequirement') {
      this.openRequirementsModal(course);
    }

    if (eventName === 'updateStatus') {
      this.openUpdateStatusModal(course);
    }

    if (eventName === 'viewDetails') {
      this.openCourseDetailsModal(course);
    }
  }

  openUpdateStatusModal = (registration: any) => {
    const dialog = this.dialog.open(UpdateStatusModalComponent, { data: registration });
    dialog.afterClosed().subscribe((result: any) => {
      if (result) {
        this.getRequirements();
        this.searchForCourses();
      }
    });
  };

  openRequirementsModal = (registration: any) => {
    const data = {
      userRequirements: this.requirements,
      course: registration,
      selectedUser: this.user,
      selectedRequirementId: registration.selectedRequirementId,
    };
    const dialog = this.dialog.open(UpdateRequirementModalComponent, { data: data });

    dialog.afterClosed().subscribe((result: any) => {
      this.getRequirements();
      this.searchForCourses();
    });
  };

  openCourseDetailsModal = (registration: any) => {
    this.dialog.open(CatalogCourseDetailsComponent, {
      data: {
        courseItemId: registration.courseItemId,
        userRequirements: this.user.Requirements,
      },
    });
  };

  handleSortClicked({
    columnName,
    sortDirection,
  }: {
    columnName: string;
    sortDirection: 'ASC' | 'DESC';
  }) {
    this.sortColumn = columnName;
    this.sortColumnDirection = sortDirection;
    this.offset = 0;

    this.getRequirements();
    this.searchForCourses();
  }

  handleNoDataButtonClicked() { }

  calculateOffset(page: number): number {
    return (page - 1) * this.selectedItemsPerPage;
  }

  // Pagination event handlers
  onPageChanged(page: number) {
    const offset = this.calculateOffset(page);
    this.offset = offset;
    this.searchForCourses();
  }

  onItemsPerPageChange(itemsPerPage: number) {
    this.selectedItemsPerPage = itemsPerPage;
    this.offset = 0;
    this.searchForCourses();
  }
}
